import { useQuery } from '@tanstack/react-query'
import { type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router'

import {
  fetchTourismHospital,
  tourismHospitalKey,
  type TourismHospitalDetail,
} from '../api/tourism'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { Footer } from '../components/Footer'
import { Icon } from '../components/Icon'
import { Navbar } from '../components/Navbar'
import { ApiError } from '../lib/api'

/**
 * Tourism hospital profile (M7-T04) at `/tourism/hospital/:slug`. The tourism
 * lens: about, specialties, accreditations, languages, indicative pricing, and
 * the international-patient-desk enquiry entry (→ M7-T05). Read-only; no GPS,
 * no maps.
 *
 * M17-T13 — restyled into the Figma visual language. The frames draw no tourism
 * profile (the section covers the home only), so this composes from the M16
 * atoms and follows the language T08 set on the Local profile: display face for
 * the name, 18/28 Medium section titles, --r-3 shells, --shadow-card.
 *
 * The hand-rolled brand bar is now the shared `Navbar`, which carries the view
 * toggle and the Request Consultation CTA; the back control stays in-flow
 * beneath it, as on the Local profile (T04 keeps back-bars with their pages).
 */
export function TourismProfile(): ReactNode {
  const { slug = '' } = useParams()
  const navigate = useNavigate()

  const query = useQuery({
    queryKey: tourismHospitalKey(slug),
    queryFn: ({ signal }) => fetchTourismHospital(slug, signal),
    retry: (count, err) =>
      err instanceof ApiError && err.code === 'not_found' ? false : count < 2,
  })
  const notFound =
    query.error instanceof ApiError && query.error.code === 'not_found'

  return (
    <main className="flex min-h-screen flex-col bg-bg-app font-sans text-ink-1">
      <Navbar tagline="International patients" />

      <div className="px-5 pt-4 sm:px-8">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1 rounded-r3 py-1 text-[14px] font-medium leading-5 text-ink-2 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="chevl" size={16} /> All partner hospitals
        </button>
      </div>

      {query.isPending ? (
        <ProfileSkeleton />
      ) : notFound ? (
        <Centered
          title="Hospital not found"
          body="That hospital isn't one of our partners."
          actionLabel="Back to tourism home"
          onAction={() => navigate('/')}
        />
      ) : query.isError ? (
        <Centered
          title="Couldn't load this hospital"
          body="Check your connection and try again."
          actionLabel="Retry"
          onAction={() => void query.refetch()}
          alert
        />
      ) : (
        <Detail
          hospital={query.data}
          onEnquire={() => navigate(`/tourism/enquiry?hospital=${slug}`)}
        />
      )}

      <Footer />
    </main>
  )
}

// Indicative USD pricing is placeholder copy — the concierge gives a real quote
// after enquiry (no pricing table in Phase 1).
//
// These are treatment prices, NOT a nightly bed rate: ADR-0027 gates bed price
// behind a bed-type choice, which is a Local journey, and a per-night number on
// a medical-tourism surface would read as a booking price for a bed this phase
// cannot sell (audit gap G25).
const INDICATIVE_PRICING: ReadonlyArray<[string, string]> = [
  ['Specialist consultation', '60 – 180'],
  ['Diagnostic imaging (MRI/CT)', '180 – 600'],
  ['Day-case procedure', '900 – 3,500'],
  ['Major surgery', 'from 6,000'],
]

function Detail({
  hospital,
  onEnquire,
}: {
  hospital: TourismHospitalDetail
  onEnquire: () => void
}): ReactNode {
  return (
    <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-8 px-5 py-6 sm:px-8 lg:grid-cols-[1fr_320px]">
      <div>
        {/* Empty until M17-T18 lands the AMTC photography. Shaped, not a grey
            box, so the page reads at its final proportions now. */}
        <div
          className="grid h-44 place-items-center rounded-r3 bg-gradient-to-br from-bg-soft to-bg-sunken text-ink-4"
          aria-hidden="true"
        >
          <Icon name="image" size={32} />
        </div>

        <span className="mt-5 block font-mono text-[11px] uppercase tracking-wide text-ink-3">
          AMTC partner · {hospital.city}
        </span>
        <h1 className="mt-1 font-display text-d-xs font-medium tracking-display text-ink-1 sm:text-d-sm">
          {hospital.name}
        </h1>
        <p className="mt-1.5 flex items-center gap-1 text-[14px] leading-5 text-ink-2">
          <Icon name="pin" size={14} /> {hospital.address}
        </p>

        {hospital.description_tourism && (
          <Section title="About">
            <p className="text-[16px] leading-6 text-ink-2">
              {hospital.description_tourism}
            </p>
          </Section>
        )}

        {hospital.specialties.length > 0 && (
          <Section title="Specialties offered">
            <div className="flex flex-wrap gap-2">
              {hospital.specialties.map((s) => (
                <span
                  key={s}
                  className="rounded-full border border-line-1 bg-bg-soft px-3 py-1 text-[14px] leading-5 text-ink-1"
                >
                  {s}
                </span>
              ))}
            </div>
          </Section>
        )}

        {hospital.accreditations.length > 0 && (
          <Section title="Accreditations">
            <div className="flex flex-wrap gap-2">
              {hospital.accreditations.map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-1.5 rounded-full bg-accent-bg px-3 py-1 text-[14px] font-medium leading-5 text-accent-ink"
                >
                  <Icon name="badge" size={14} /> {a}
                </span>
              ))}
            </div>
          </Section>
        )}

        {hospital.languages.length > 0 && (
          <Section title="Languages spoken">
            <div className="flex flex-wrap gap-2">
              {hospital.languages.map((l) => (
                <span
                  key={l}
                  className="rounded-full bg-bg-soft px-3 py-1 text-[14px] leading-5 text-ink-1"
                >
                  {l}
                </span>
              ))}
            </div>
          </Section>
        )}

        <Section title="Indicative pricing · USD">
          <div className="overflow-hidden rounded-r3 border border-line-1">
            {INDICATIVE_PRICING.map(([item, price], i) => (
              <div
                key={item}
                className={
                  i === 0
                    ? 'flex justify-between gap-4 px-4 py-3 text-[16px] leading-6'
                    : 'flex justify-between gap-4 border-t border-line-1 px-4 py-3 text-[16px] leading-6'
                }
              >
                <span>{item}</span>
                <span className="tabular-nums text-ink-2">${price}</span>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[14px] leading-5 text-ink-3">
            Indicative only. The AMTC concierge provides a personalised quote
            after your enquiry.
          </p>
        </Section>
      </div>

      <aside className="self-start rounded-r3 border border-line-1 bg-bg-app p-5 shadow-card lg:sticky lg:top-4">
        <div className="font-mono text-[11px] uppercase tracking-wide text-ink-3">
          International patient desk
        </div>
        <h2 className="mt-2 text-[18px] font-medium leading-7 text-ink-1">
          Plan your visit with {hospital.name}
        </h2>
        <p className="mt-2 text-[14px] leading-5 text-ink-2">
          Send your case to the AMTC concierge. They&apos;ll match you to a
          specialist, propose dates, and reply within one business day.
        </p>
        <Button full className="mt-4" icon="chat" onClick={onEnquire}>
          Enquire
        </Button>
        <div className="mt-4 flex flex-col gap-2 border-t border-line-1 pt-4 text-[14px] leading-5 text-ink-3">
          {hospital.international_desk_phone && (
            <a
              href={`tel:${hospital.international_desk_phone}`}
              className="inline-flex items-center gap-1.5 hover:text-ink-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
              <Icon name="phone" size={14} />{' '}
              {hospital.international_desk_phone}
            </a>
          )}
          <span className="inline-flex items-center gap-1.5">
            <Icon name="lock" size={14} /> Encrypted · concierge-only access
          </span>
        </div>
      </aside>
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: ReactNode
}): ReactNode {
  return (
    <section className="mt-8">
      {/* PORTED — fgType.lg at Medium, over a --line-1 rule. The incumbent was
          14px semibold. */}
      <h2 className="mb-3 border-b border-line-1 pb-2 text-[18px] font-medium leading-7 text-ink-1">
        {title}
      </h2>
      {children}
    </section>
  )
}

function ProfileSkeleton(): ReactNode {
  return (
    <div
      role="status"
      aria-label="Loading hospital"
      className="mx-auto w-full max-w-5xl px-5 py-6 sm:px-8"
    >
      <div className="h-44 animate-pulse rounded-r3 bg-bg-sunken motion-reduce:animate-none" />
      <div className="mt-5 h-8 w-72 max-w-full animate-pulse rounded-r3 bg-bg-sunken motion-reduce:animate-none" />
      <div className="mt-3 h-5 w-56 max-w-full animate-pulse rounded-r3 bg-bg-sunken motion-reduce:animate-none" />
      <div className="mt-8 h-40 w-full animate-pulse rounded-r3 bg-bg-sunken motion-reduce:animate-none" />
    </div>
  )
}

function Centered({
  title,
  body,
  actionLabel,
  onAction,
  alert,
}: {
  title: string
  body: string
  actionLabel: string
  onAction: () => void
  alert?: boolean
}): ReactNode {
  return (
    <div
      {...(alert ? { role: 'alert' } : {})}
      className="mx-auto w-full max-w-xl px-5 py-16 sm:px-8"
    >
      <EmptyState
        title={title}
        body={body}
        action={
          <Button kind="secondary" onClick={onAction}>
            {actionLabel}
          </Button>
        }
      />
    </div>
  )
}
